// src/AudioRecognition/audioStream.ts
export interface StreamHandle {
  stop(): void;
  context: AudioContext;
}

export interface AudioBlock {
  samples: Float32Array;
  sampleRate: number;
  timeSec: number;
}

export async function openMicStream(
  onAudio: (block: AudioBlock) => void,
  bufferSize = 2048,
): Promise<StreamHandle> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      channelCount: 1,
    },
  });

  const context = new AudioContext({ sampleRate: 16000 });
  const source = context.createMediaStreamSource(stream);
  let resumeCleanup: (() => void) | null = null;
  let teardownNode: (() => void) | null = null;

  const clearResumeListeners = () => {
    resumeCleanup?.();
    resumeCleanup = null;
  };

  const resumeContext = () => {
    void context
      .resume()
      .then(() => {
        if (context.state === 'running') {
          clearResumeListeners();
        }
      })
      .catch(() => {
        // Ignore resume failures; the next user interaction will retry.
      });
  };

  if (typeof window !== 'undefined') {
    const resumeOnInteraction = () => {
      resumeContext();
    };

    window.addEventListener('pointerdown', resumeOnInteraction, { capture: true });
    window.addEventListener('touchstart', resumeOnInteraction, { capture: true });
    window.addEventListener('keydown', resumeOnInteraction, { capture: true });

    resumeCleanup = () => {
      window.removeEventListener('pointerdown', resumeOnInteraction, { capture: true });
      window.removeEventListener('touchstart', resumeOnInteraction, { capture: true });
      window.removeEventListener('keydown', resumeOnInteraction, { capture: true });
    };
  }

  resumeContext();

  const connectScriptProcessorFallback = () => {
    const proc = context.createScriptProcessor(bufferSize, 1, 1);
    const sink = context.createGain();
    sink.gain.value = 0;
    const buf = new Float32Array(bufferSize);

    source.connect(proc);
    proc.connect(sink);
    sink.connect(context.destination);

    proc.onaudioprocess = (e: AudioProcessingEvent) => {
      e.inputBuffer.copyFromChannel(buf, 0, 0);
      onAudio({
        samples: buf.slice(),
        sampleRate: context.sampleRate,
        timeSec: context.currentTime,
      });
    };

    teardownNode = () => {
      try {
        proc.disconnect();
      } catch (error) {
        void error;
      }
      try {
        sink.disconnect();
      } catch (error) {
        void error;
      }
    };
  };

  if (context.audioWorklet && typeof AudioWorkletNode !== 'undefined') {
    try {
      const workletModuleUrl = new URL('./capture/mic-frame-processor.worklet.js', import.meta.url)
        .href;
      await context.audioWorklet.addModule(workletModuleUrl);

      const node = new AudioWorkletNode(context, 'museflow-mic-frame-processor', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        channelCount: 1,
      });
      const sink = context.createGain();
      sink.gain.value = 0;

      node.port.onmessage = (event: MessageEvent<AudioBlock>) => {
        const samples = event.data?.samples;
        if (!(samples instanceof Float32Array) || samples.length === 0) return;
        onAudio({
          samples,
          sampleRate: event.data.sampleRate ?? context.sampleRate,
          timeSec: event.data.timeSec ?? context.currentTime,
        });
      };

      source.connect(node);
      node.connect(sink);
      sink.connect(context.destination);

      teardownNode = () => {
        try {
          node.port.onmessage = null;
        } catch (error) {
          void error;
        }
        try {
          node.disconnect();
        } catch (error) {
          void error;
        }
        try {
          sink.disconnect();
        } catch (error) {
          void error;
        }
      };
    } catch (error) {
      console.warn(
        '[AudioRecognition] AudioWorklet init failed, falling back to ScriptProcessor.',
        error,
      );
      connectScriptProcessorFallback();
    }
  } else {
    connectScriptProcessorFallback();
  }

  return {
    context,
    stop() {
      clearResumeListeners();
      teardownNode?.();
      teardownNode = null;
      try {
        source.disconnect();
      } catch (error) {
        void error;
      }
      for (const t of stream.getTracks()) {
        try {
          t.stop();
        } catch (error) {
          void error;
        }
      }
      try {
        context.close();
      } catch (error) {
        void error;
      }
    },
  };
}
